import { ApiSubscriptionMerchant } from "@incendium/api";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import GlassCard from "Components/GlassCard/GlassCard";
import StyledDrawer, {
  StyledDrawerContainer,
} from "Components/UI/StyledDrawer";
import { centsToDollars } from "Helpers/numbers";
import { ReactNode, useCallback, useMemo, useState } from "react";

const DESCRIPTION_COLLAPSED_HEIGHT = 39; // ~2 lines of body2

interface IBaseSubscriptionPaymentCardProps {
  /** Product name — the plan group (e.g. Starter, Growth, Pro) */
  name: string;
  /** Product marketing description shared across tiers of this product */
  description: string;
  /** Subscription tier details within the product (e.g. order/pageview limits) */
  planDescription?: string;
  price: number; // in cents
  annualPrice?: number; // in cents
  /** When true, highlight annual pricing as the active billing period */
  isAnnual?: boolean;
  merchant: ApiSubscriptionMerchant;
  paid?: boolean;
  hidePayment?: boolean;
  qr?: string | null; // base64
  dense?: boolean;
  /** Slot for volume/tier controls (e.g. future order-volume slider) */
  volumeControl?: ReactNode;
  onPayClick?: () => void;
}

interface IWithPaymentProps extends IBaseSubscriptionPaymentCardProps {
  hidePayment?: false;
  terms: string;
  action?: undefined;
  onPayClick: () => void;
}

interface IWithoutPaymentProps extends IBaseSubscriptionPaymentCardProps {
  hidePayment: true;
  terms?: undefined;
  action: ReactNode;
}

type ISubscriptionPaymentCardProps = IWithPaymentProps | IWithoutPaymentProps;

function SubscriptionPaymentCard({
  name,
  description,
  planDescription,
  price,
  annualPrice,
  isAnnual,
  terms,
  qr,
  paid,
  hidePayment,
  action,
  merchant,
  dense,
  volumeControl,
  onPayClick,
}: ISubscriptionPaymentCardProps) {
  const [termsAccepeted, setTermsAccepeted] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  const parsedTerms = useMemo(() => {
    const textArea = document.createElement("textarea");
    textArea.innerHTML = terms || "";
    return textArea.value;
  }, [terms]);

  const descriptionLines = useMemo(
    () => (description ? description.split("\n").filter(Boolean) : []),
    [description],
  );

  const merchantLabel =
    merchant === ApiSubscriptionMerchant.SUBSCRIPTION_MERCHANT_SHOPIFY
      ? "Shopify"
      : "Stripe";

  return (
    <GlassCard>
      <Stack spacing={dense ? 1.5 : 2}>
        {/* Product — plan group */}
        <Stack spacing={1}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={2}
          >
            <Stack spacing={0.75} minWidth={0}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ letterSpacing: 1.2, lineHeight: 1 }}
              >
                Plan
              </Typography>
              <Typography variant="subtitle1" component="h3">
                {name}
              </Typography>
            </Stack>
            <Stack alignItems="flex-end" flexShrink={0}>
              <Stack direction="row" alignItems="baseline" spacing={0.25}>
                <Typography variant="h4" component="p" sx={{ fontWeight: 700 }}>
                  <Typography
                    component="span"
                    variant="h6"
                    sx={{ fontWeight: 700, mr: 0.25 }}
                  >
                    $
                  </Typography>
                  {centsToDollars(
                    isAnnual && (annualPrice || 0) > 0 ? annualPrice || 0 : price
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isAnnual && (annualPrice || 0) > 0 ? "/yr" : "/mo"}
                </Typography>
              </Stack>
              {isAnnual && (annualPrice || 0) > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  USD · billed annually
                  {price > 0
                    ? ` · or $${centsToDollars(price)} /mo`
                    : ""}
                </Typography>
              ) : (annualPrice || 0) > 0 ? (
                <Typography variant="caption" color="primary" fontWeight={700}>
                  or ${centsToDollars(annualPrice)} /yr
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  USD · billed monthly
                </Typography>
              )}
            </Stack>
          </Stack>

          {descriptionLines.length > 0 ? (
            <Accordion
              disableGutters
              elevation={0}
              expanded={descriptionExpanded}
              onChange={(_, expanded) => setDescriptionExpanded(expanded)}
              sx={{
                background: "transparent",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore fontSize="small" />}
                sx={{
                  minHeight: descriptionExpanded
                    ? undefined
                    : DESCRIPTION_COLLAPSED_HEIGHT,
                  px: 0,
                  py: 0,
                  alignItems: "flex-start",
                  "& .MuiAccordionSummary-content": {
                    my: 0,
                    mr: 1,
                    overflow: "hidden",
                    width: "100%",
                  },
                  "& .MuiAccordionSummary-expandIconWrapper": {
                    mt: 0.25,
                  },
                }}
              >
                {descriptionExpanded ? (
                  <Typography variant="caption" color="text.secondary">
                    About this plan
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      height: DESCRIPTION_COLLAPSED_HEIGHT,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {descriptionLines.map((s, i) => (
                      <Typography
                        key={`desc-preview-${i}`}
                        variant="body2"
                        color="text.secondary"
                        display="block"
                      >
                        {s}
                      </Typography>
                    ))}
                  </Box>
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0, pt: 0.5, pb: 0 }}>
                <Stack spacing={0.5}>
                  {descriptionLines.map((s, i) => (
                    <Typography
                      key={`desc-full-${i}`}
                      variant="body2"
                      color="text.secondary"
                    >
                      {s}
                    </Typography>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ) : (
            <Box sx={{ height: DESCRIPTION_COLLAPSED_HEIGHT }} />
          )}
        </Stack>

        <Divider />

        {/* Subscription — volume / tier within the product */}
        <Stack>
          {planDescription ? (
            planDescription.split("\n").map((s, i) => (
              <Typography
                key={`plan-${i}`}
                variant="subtitle2"
              // sx={{ lineHeight: 1.35 }}
              >
                {s}
              </Typography>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              Volume details unavailable
            </Typography>
          )}

          {/* Reserved for future volume slider / tier picker */}
          {volumeControl ? <Box pt={0.5}>{volumeControl}</Box> : null}
        </Stack>

        {!hidePayment && (
          <>
            <Divider />
            {paid ? (
              <Box>
                <Alert severity="success">Payment Process Successfully</Alert>
                <Typography variant="body2" mt={1}>
                  Your access will be ready in a few minutes. If you cannot gain
                  acess please submit a support ticket and we will look into it.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                <Stack alignItems="center" spacing={1}>
                  <Button
                    onClick={onPayClick}
                    disabled={!termsAccepeted}
                    size="large"
                    fullWidth={!!dense}
                  >
                    Pay with {merchantLabel}
                  </Button>
                  {/* tmp disabling qr code */}
                  {!true &&
                    (qr ? (
                      <img
                        style={{ opacity: termsAccepeted ? 1 : 0.05 }}
                        width={"100%"}
                        src={qr}
                        alt="Scan QR to pay on Mobile"
                      />
                    ) : (
                      <p>Loading image...</p>
                    ))}
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                >
                  <FormControlLabel
                    componentsProps={{
                      typography: {
                        variant: "body1",
                      },
                    }}
                    control={
                      <Switch
                        checked={termsAccepeted}
                        onChange={() => {
                          setTermsAccepeted(true);
                        }}
                      />
                    }
                    label="Accept"
                    sx={{ marginRight: 0 }}
                  />
                  <Button onClick={() => setOpen(true)} variant="text">
                    terms and conditions
                  </Button>
                </Stack>
                <Alert severity="warning">
                  You shall be redirected to {merchantLabel}, on payment you
                  shall be brought back to Incendium.
                </Alert>
              </Stack>
            )}
          </>
        )}

        {action}
      </Stack>
      <StyledDrawer open={open} onClose={onClose}>
        <StyledDrawerContainer width={1100}>
          <div dangerouslySetInnerHTML={{ __html: parsedTerms || "" }} />
        </StyledDrawerContainer>
      </StyledDrawer>
    </GlassCard>
  );
}

export default SubscriptionPaymentCard;
